import {
  GRID_CHECKBOX_SELECTION_FIELD,
  GridColDef,
  gridPaginatedVisibleSortedGridRowIdsSelector,
  GridPaginationModel,
  GridRowSelectionModel,
  GridValidRowModel,
  useGridApiRef
} from '@mui/x-data-grid';
import { useCallback, useMemo, useRef } from 'react';
import { IS_BULK_ACTIONS_ENABLED } from 'v2/constants';
import { pinSelectedRows, withSelectedRowsFirst } from './dataGridRowSelectionUtils';
import { getRowSelectionDataGridProps } from './getRowSelectionDataGridProps';
import { useDataGridRowReorderAnimation } from './useDataGridRowReorderAnimation';
import { useDataGridRowSelection } from './useDataGridRowSelection';

interface Params<RowType extends GridValidRowModel> {
  allRows: RowType[];
  filteredRows: RowType[];
}

/**
 * Row selection for an overview DataGrid: selected rows are pinned to the top (even when a filter hides them),
 * selecting jumps back to the first page, and changing the page clears the selection.
 */
export const useDataGridSelectionWithPagination = <RowType extends GridValidRowModel & { id: string }>({
  allRows,
  filteredRows
}: Params<RowType>) => {
  const gridRef = useRef<HTMLDivElement>(null);
  const apiRef = useGridApiRef();
  const currentPageRef = useRef(0);
  const { captureRowPositions } = useDataGridRowReorderAnimation(gridRef, apiRef);

  const {
    rowSelectionModel,
    selectedIds,
    selectedCount,
    onRowSelectionModelChange,
    onPaginationModelChange,
    clearSelection
  } = useDataGridRowSelection({ onBeforeSelectionChange: captureRowPositions });

  const onRowSelectionChange = useCallback(
    (nextRowSelectionModel: GridRowSelectionModel) => {
      onRowSelectionModelChange(nextRowSelectionModel);

      if (currentPageRef.current === 0) return;

      currentPageRef.current = 0;
      apiRef.current?.setPage?.(0);
    },
    [apiRef, onRowSelectionModelChange]
  );

  const onSelectAllCurrentPage = useCallback(() => {
    const pageRowIds = gridPaginatedVisibleSortedGridRowIdsSelector(apiRef);
    onRowSelectionChange({ type: 'include', ids: new Set(pageRowIds) });
  }, [apiRef, onRowSelectionChange]);

  const onPaginationChange = useCallback(
    (nextPaginationModel: GridPaginationModel) => {
      const pageChanged = nextPaginationModel.page !== currentPageRef.current;
      currentPageRef.current = nextPaginationModel.page;

      if (!pageChanged) return;

      onPaginationModelChange();
    },
    [onPaginationModelChange]
  );

  const rows = useMemo(() => pinSelectedRows(allRows, filteredRows, selectedIds), [allRows, filteredRows, selectedIds]);

  /** Spread on the DataGrid: columns (with the checkbox column), selection props and the selection class name. */
  const getGridProps = (columns: GridColDef<RowType>[], className?: string) => {
    const selectionClassName = [className, IS_BULK_ACTIONS_ENABLED && 'bulk-actions-enabled'].filter(Boolean).join(' ');

    if (!IS_BULK_ACTIONS_ENABLED) return { columns, rowSelection: false, className: selectionClassName };

    return {
      ...getRowSelectionDataGridProps({
        columns: withSelectedRowsFirst(columns, selectedIds),
        rowSelectionModel,
        onRowSelectionModelChange: onRowSelectionChange,
        onPaginationModelChange: onPaginationChange
      }),
      className: selectionClassName
    };
  };

  /** Use in `onRowClick` to ignore clicks on the selection checkbox. */
  const isCheckboxClick = (event: { target: EventTarget | null }) =>
    (event.target as HTMLElement).closest(`[data-field="${GRID_CHECKBOX_SELECTION_FIELD}"]`) !== null;

  /** Spread on `DataGridSelectionToolbar`. */
  const toolbarProps = { selectedCount, onSelectAllCurrentPage, onClearSelection: clearSelection };

  return {
    getGridProps,
    isCheckboxClick,
    toolbarProps,
    gridRef,
    apiRef,
    rows,
    rowSelectionModel,
    selectedIds,
    selectedCount,
    onRowSelectionChange,
    onSelectAllCurrentPage,
    onPaginationChange,
    clearSelection
  };
};

export type DataGridSelectionWithPagination<RowType extends GridValidRowModel & { id: string }> = ReturnType<
  typeof useDataGridSelectionWithPagination<RowType>
>;
